import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle2, Smartphone, Zap, TrendingUp } from 'lucide-react';

// Tu Parcero Official Logo
const LOGO_URL = 'img/Tu Parcero Cabeza.png';

async function signupApi(action, body) {
  const response = await fetch('/onboarding/api/' + action, { method: body ? 'POST' : 'GET', credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : {}, ...(body ? { body: JSON.stringify(body) } : {}) });
  const data = await response.json();
  if (!response.ok) throw Error(data.error || 'No se pudo iniciar el registro.');
  return data;
}

export const SignupPage = () => {
  const [ready, setReady] = useState(false);
  const [signupMessage, setSignupMessage] = useState('');
  const entry = useRef({});
  useEffect(() => {
    let disposed = false;
    const state = entry.current = {};
    const prepare = async () => {
      const config = await signupApi('entry');
      if (disposed) return;
      state.view = config.view;
      if (config.view === 'onboarding') { setReady(true); return; }
      const status = await signupApi('status');
      if (disposed) return;
      state.resume = status.connected && !status.appId;
      if (state.resume) { setReady(true); return; }
      state.config = await signupApi('start', { mode: 'migration' });
      if (disposed) return;
      if (!window.FB) await new Promise((resolve, reject) => {
        let script = document.getElementById('facebook-sdk');
        const existing = !!script;
        if (!script) { script = document.createElement('script'); script.id = 'facebook-sdk'; script.async = true; script.src = 'https://connect.facebook.net/es_LA/sdk.js'; }
        script.addEventListener('load', resolve, { once: true });
        script.addEventListener('error', () => { script.remove(); reject(Error('No se pudo cargar Meta. Recarga la página para intentarlo de nuevo.')); }, { once: true });
        if (!existing) document.head.appendChild(script);
      });
      if (disposed) return;
      window.FB.init({ appId: state.config.appId, version: state.config.version, autoLogAppEvents: false, xfbml: false });
      setReady(true);
    };
    state.cancel = message => {
      clearTimeout(state.run?.timeout); state.run = null;
      if (!disposed) { setSignupMessage(message); setReady(true); }
    };
    state.complete = async () => {
      const run = state.run;
      if (!run || run.saving || !run.code || !run.assets) return;
      run.saving = true; clearTimeout(run.timeout);
      setSignupMessage('Guardando la conexión de tu negocio…');
      try {
        await signupApi('complete', { code: run.code, state: state.config.state, waba_id: run.assets.waba_id, phone_number_id: run.assets.phone_number_id });
        if (!disposed) window.location.href = '/onboarding';
      } catch (error) {
        if (disposed) return;
        state.run = null;
        try { await prepare(); } catch (retryError) { setSignupMessage(retryError.message); return; }
        setSignupMessage(error.message);
      }
    };
    const handleMessage = event => {
      if (!state.run || !['https://www.facebook.com', 'https://web.facebook.com', 'https://business.facebook.com'].includes(event.origin)) return;
      let data; try { data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; } catch { return; }
      if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
      if (['FINISH', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'].includes(data.event)) { state.run.assets = data.data || {}; void state.complete(); }
      else if (['CANCEL', 'ERROR'].includes(data.event)) state.cancel(data.event === 'CANCEL' ? 'La conexión fue cancelada. Puedes intentarlo otra vez.' : 'Meta no pudo completar la conexión. Revisa los permisos e inténtalo otra vez.');
    };
    window.addEventListener('message', handleMessage);
    void prepare().catch(error => { if (!disposed) setSignupMessage(error.message); });
    const credlyScript = document.createElement('script');
    credlyScript.src = '//cdn.credly.com/assets/utilities/embed.js';
    credlyScript.async = true;
    document.body.appendChild(credlyScript);
    return () => { disposed = true; clearTimeout(state.run?.timeout); state.run = null; window.removeEventListener('message', handleMessage); credlyScript.remove(); };
  }, []);

  const launchWhatsAppSignup = () => {
    const state = entry.current;
    if (!ready) return;
    if (state.view === 'onboarding' || state.resume) { window.location.href = '/onboarding'; return; }
    const run = state.run = {};
    setReady(false); setSignupMessage('Continúa en la ventana de Meta para conectar tu línea con la API.');
    run.timeout = setTimeout(() => { if (state.run === run && !run.saving) state.cancel('La autorización venció. Recarga la página para iniciar otra conexión.'); }, 1800000);
    try {
      // Keep FB.login synchronous with the click so the browser allows the popup.
      window.FB.login(response => {
        if (state.run !== run) return;
        if (response.authResponse?.code) { run.code = response.authResponse.code; void state.complete(); }
        else state.cancel('No se autorizó la conexión. Puedes intentarlo otra vez.');
      }, { config_id: state.config.configId, response_type: 'code', override_default_response_type: true, state: state.config.state, extras: { setup: {} } });
    } catch (error) { state.cancel(error.message); }
  };

  const handleLogin = () => {
    window.location.href = '/web';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img src={LOGO_URL} alt="Tu Parcero" className="h-16 w-auto" />
            </div>
            <Button
              onClick={handleLogin}
              variant="outline"
              className="border-[#FFFB5C] text-[#FFFB5C] hover:bg-[#FFFB5C] hover:text-black transition-all duration-300 font-semibold"
            >
              Iniciar sesión
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h1 className="text-5xl md:text-7xl font-bold text-[#6BFFF7] leading-tight">
                  Deja que{' '}
                  <span className="text-[#6BFFF7]">Tu Parcero</span>{' '}
                  lo haga por ti
                </h1>
                <p className="text-xl text-gray-300 leading-relaxed">
                  Automatización e inteligencia artificial para el futuro de tu negocio.
                  Optimiza la interacción con clientes las 24/7.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={launchWhatsAppSignup}
                  disabled={!ready}
                  size="lg"
                  className="bg-[#FFFB5C] hover:bg-[#FFE85C] text-black text-lg px-8 py-7 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 font-bold"
                >
                  Registrarme como cliente
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={handleLogin}
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-700 text-gray-300 hover:border-[#FFFB5C] hover:text-[#FFFB5C] text-lg px-8 py-7 rounded-2xl transition-all duration-300 bg-transparent hover:bg-transparent"
                >
                  Iniciar sesión
                </Button>
              </div>

              {signupMessage && <p role="status" aria-live="polite" className="text-[#FFFB5C]">{signupMessage}</p>}

              <div className="flex items-center space-x-6 pt-4">
                <div className="flex -space-x-2">
                  {[
                    { src: 'img/socios/logo_expenzen.png', alt: 'Expenzen - Gestión de gastos personales' },
                    { src: 'img/socios/logo_info_voto.png', alt: 'Info Voto - Transparencia electoral' },
                    { src: 'img/socios/logo_mati.png', alt: 'MATI - Movilidad Inteligente' },
                    { src: 'img/socios/logo_cata.png', alt: 'Cata - Agencia Catastral de Cundinamarca' },
                    { src: 'img/socios/logo_coopemtol.png', alt: 'Coopemtol - Cooperativa de maestros del tolima' },
                    { src: 'img/socios/logo_atos.png', alt: 'Atos Inmobiliaria' },
                  ].map((logo, i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#0a0a0a] bg-white flex items-center justify-center"
                    >
                      <img
                        src={logo.src}
                        alt={logo.alt}
                        title={logo.alt}
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">úntete a las empresas que confían en nosotros</p>
                  <p className="text-xs text-gray-500">Reconocidos por APPS.CO 2023</p>
                  <p className="text-xs text-gray-500">Reconocidos por PotencIA Digital 2024</p>
                </div>
              </div>
            </div>

            {/* Robot Illustration */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#FFFB5C]/10 to-[#20C9AC]/10 rounded-3xl blur-3xl"></div>
              <div className="relative bg-[#1a1a1a] rounded-3xl p-12 shadow-2xl border border-[#2a2a2a]">
                <div className="flex justify-center">
                  <img
                    src="img/robot-illustration.png"
                    alt="Robot Illustration"
                    className="w-full h-auto max-w-md"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-[#0f0f0f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Beneficios</h2>
            <p className="text-xl text-gray-400">Transforma tu negocio con inteligencia artificial</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-[#1a1a1a] p-8 rounded-2xl border border-[#2a2a2a] hover:border-[#FFFB5C]/50 transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-14 h-14 bg-[#FFFB5C] rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="h-7 w-7 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Reducción de Costos</h3>
              <p className="text-gray-400 leading-relaxed">
                Optimización que mejora la rentabilidad y uso de recursos de tu empresa.
              </p>
            </div>

            <div className="bg-[#1a1a1a] p-8 rounded-2xl border border-[#2a2a2a] hover:border-[#FFFB5C]/50 transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-14 h-14 bg-[#FFFB5C] rounded-xl flex items-center justify-center mb-6">
                <Zap className="h-7 w-7 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Automatización Continua</h3>
              <p className="text-gray-400 leading-relaxed">
                Respuesta y asistencia para tus clientes las 24 horas, todos los días del año.
              </p>
            </div>

            <div className="bg-[#1a1a1a] p-8 rounded-2xl border border-[#2a2a2a] hover:border-[#FFFB5C]/50 transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-14 h-14 bg-[#FFFB5C] rounded-xl flex items-center justify-center mb-6">
                <CheckCircle2 className="h-7 w-7 text-black" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Escalabilidad Adaptable</h3>
              <p className="text-gray-400 leading-relaxed">
                Crece con tecnología accesible para cada etapa de tu negocio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#006A71]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge Credly */}
          <div className="flex justify-center mb-6">
            <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#FFFB5C] to-[#20C9AC]">
              <div className="bg-white rounded-2xl p-4">
                <div
                  data-iframe-width="150"
                  data-iframe-height="240"
                  data-share-badge-id="a0416bd4-956b-4ef9-9639-268a95b3e9bb"
                  data-share-badge-host="https://www.credly.com"
                ></div>
              </div>
            </div>
          </div>
          <Smartphone className="h-16 w-16 text-[#FFFB5C] mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            ¿Estás listo para llevar tu negocio al siguiente nivel?
          </h2>
          <p className="text-xl text-gray-100 mb-8 leading-relaxed">
            Con nosotros, mejorarás la experiencia de tus clientes y optimizarás la gestión de servicios con una solución tecnológica escalable.
          </p>
          <Button
            onClick={launchWhatsAppSignup}
                  disabled={!ready}
            size="lg"
            className="bg-[#FFFB5C] hover:bg-[#FFE85C] text-black text-lg px-10 py-7 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 font-bold"
          >
            Comenzar ahora
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      /* Footer */
      <footer className="bg-black text-white py-12 border-t border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <img src={LOGO_URL} alt="Tu Parcero" className="h-12 w-auto mb-4" />
              <p className="text-gray-400 text-sm leading-relaxed">
                Soluciones tecnológicas basadas en inteligencia artificial para optimizar tu negocio.
              </p>
            </div>

            <div>
              <h3 className="font-bold text-[#FFFB5C] mb-4">Contacto</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>WhatsApp: +57 320 2039068</li>
                <li>Email: atencion@tuparcero.com</li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-[#FFFB5C] mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="/terms?tab=terms" className="hover:text-[#FFFB5C] transition-colors">Términos y condiciones</a></li>
                <li><a href="/terms?tab=privacy" className="hover:text-[#FFFB5C] transition-colors">Política de privacidad</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#1a1a1a] mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>
              © {new Date().getFullYear()} Tu Parcero - Soluciones Tecnológicas. Todos los derechos reservados.
            </p>
            <p className="mt-2 text-gray-500">
              Reconocidos por APPS.CO 2023 y PotencIA Digital 2024 como una de las startups más innovadoras de Colombia.
            </p>
          </div>
        </div>
      </footer>

      {/* Facebook Domain Verification */}
      <meta name="facebook-domain-verification" content="3gn48rrbn1ynurltrnupvr2c0mcrrx" />
    </div>
  );
};